import React, { useState, useEffect } from "react";
import "./Profile.css";
import Header from "../../components/common/Header";
import { FiEdit2 } from "react-icons/fi";
import { LuPencil, LuTrash2 } from "react-icons/lu";
import config from "../../config";
import { IoPersonOutline, IoSettingsOutline, IoBusinessOutline, IoPeopleOutline } from "react-icons/io5";
import SuccessPopup from "../../components/common/SuccessPopup";
import ErrorPopup from "../../components/common/ErrorPopup";

const Profile = ({ setActivePage }) => {
  const [activeTab, setActiveTab] = useState("Profile");
  const fileInputRef = React.useRef(null);
  const [profileImage, setProfileImage] = useState(localStorage.getItem("profileImage") || "icons/user.png");
  const [rawImageName, setRawImageName] = useState(localStorage.getItem("rawProfileImage") || "");
  const [isUploading, setIsUploading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [userDetails, setUserDetails] = useState({
    name: "",
    role: "",
    email: "",
    phone: "",
  });

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const token = localStorage.getItem("Authorization") || "";
        const email = localStorage.getItem("userEmail") || "";
        if (!email) throw new Error("No user email found in storage");

        const url = `${config.API_BASE_URL}${config.PROFILE_ENDPOINT}?email=${email}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) throw new Error("Failed to fetch profile");

        const fullResponse = await response.json();
        const resData = fullResponse.body || fullResponse; // Handle case where data is wrapped in 'body'

        console.log("Profile Data received:", resData);

        // Map API response to userDetails
        const mappedData = {
          name: resData.username || resData.name || `${resData.firstName || ""} ${resData.lastName || ""}`.trim() || localStorage.getItem("firstName") + " " + localStorage.getItem("lastName"),
          phone: resData.phoneNumber || resData.phone || localStorage.getItem("phoneNumber") || "",
          email: resData.emailId || resData.email || email,
          role: resData.role || localStorage.getItem("role") || "ADMIN",
        };

        // Sync back to local storage
        if (resData.firstName) localStorage.setItem("firstName", resData.firstName);
        if (resData.lastName) localStorage.setItem("lastName", resData.lastName);
        if (resData.phoneNumber) localStorage.setItem("phoneNumber", resData.phoneNumber);
        if (resData.role) localStorage.setItem("role", resData.role);

        // Fetch View Pre-signed URL if image exists
        const imageName = resData.image || resData.profileImage || resData.imageUrl;
        if (imageName && imageName !== "icons/user.png") {
          setRawImageName(imageName);
          localStorage.setItem("rawProfileImage", imageName);
          try {
            const viewUrlResponse = await fetch(
              `${config.API_BASE_URL}${config.VIEW_PRESIGNED_URL_ENDPOINT}?filename=${encodeURIComponent(imageName)}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
            if (viewUrlResponse.ok) {
              let viewUrl = await viewUrlResponse.text();
              if (viewUrl.startsWith('"') && viewUrl.endsWith('"')) {
                viewUrl = JSON.parse(viewUrl);
              }
              setProfileImage(viewUrl);
              localStorage.setItem("profileImage", viewUrl); // Cache the view URL locally
            }
          } catch (viewErr) {
            console.error("Error fetching view URL:", viewErr);
          }
        } else {
          // Reset if no image
          const defaultIcon = "icons/user.png";
          setProfileImage(defaultIcon);
          setRawImageName("");
          localStorage.removeItem("profileImage");
          localStorage.removeItem("rawProfileImage");
        }

        setUserDetails(mappedData);
      } catch (err) {
        console.error("Profile fetch error:", err);
        // Fallback to local storage if API fails
        const firstName = localStorage.getItem("firstName") || "";
        const lastName = localStorage.getItem("lastName") || "";
        const role = localStorage.getItem("role")?.toUpperCase() || "ADMIN";
        const email = localStorage.getItem("userEmail") || "";
        const phone = localStorage.getItem("phoneNumber") || "";

        setUserDetails({
          name: `${firstName} ${lastName}`.trim() || "User",
          role: role,
          email: email,
          phone: phone,
        });
      }
    };

    fetchProfileData();
  }, []);
  
  const handleRemoveImage = async () => {
    if (!window.confirm("Are you sure you want to remove your profile picture?")) return;
    
    setIsUploading(true);
    setErrorMsg("");
    
    try {
      const token = localStorage.getItem("Authorization") || "";
      const email = localStorage.getItem("userEmail") || "";
      
      // 1. Delete image from S3 if exists
      if (rawImageName && rawImageName !== "icons/user.png") {
        try {
          const deleteResponse = await fetch(`${config.API_BASE_URL}${config.DELETE_FILE_ENDPOINT}?filename=${encodeURIComponent(rawImageName)}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` }
          });
          if (!deleteResponse.ok) console.warn("Failed to delete image from S3");
        } catch (delErr) {
          console.error("Error deleting image from S3:", delErr);
        }
      }
      
      // 2. Update backend record to null
      const updateImageUrl = `${config.API_BASE_URL}${config.UPDATE_USER_IMAGE_ENDPOINT}?emailId=${email}&imageUrl=`;
      
      const updateResponse = await fetch(
        updateImageUrl,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      if (!updateResponse.ok) throw new Error("Failed to update profile record");
      
      // 3. Reset local state
      const defaultIcon = "icons/user.png";
      setProfileImage(defaultIcon);
      setRawImageName("");
      localStorage.removeItem("profileImage");
      localStorage.removeItem("rawProfileImage");
      
      setSuccessMsg("Profile picture removed successfully!");
    } catch (err) {
      console.error("Error removing profile image:", err);
      setErrorMsg(err.message || "Failed to remove profile picture.");
    } finally {
      setIsUploading(false);
    }
  };

  const tabs = [
    { id: "Profile", label: "Profile", icon: <IoPersonOutline /> },
    // { id: "Settings", label: "Settings", icon: <IoSettingsOutline /> },
    // { id: "Organization details", label: "Organization details", icon: <IoBusinessOutline /> },
    // { id: "User management", label: "User management", icon: <IoPeopleOutline /> },
  ];

  return (
    <div className="profile-page">
      <Header
        title="Profile"
        subtitle="User Information"
        onProfileClick={() => setActivePage("Profile")}
        profileImage={profileImage}
        showBack={true}
        onBack={() => setActivePage("Dashboard")}
      />

      <div className="profile-container">
        <div className="profile-tabs">
          {tabs.map((tab) => (
            <div
              key={tab.id}
              className={`profile-tab ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              {tab.label}
            </div>
          ))}
        </div>

        <div className="profile-content">
          {activeTab === "Profile" && (
            <div className="profile-details-section">
              <button className="edit-btn">
                <FiEdit2 size={16} />
                Edit
              </button>

              <div className="profile-main-layout">
                <div className="avatar-container">
                  <div className="avatar-wrapper">
                    {isUploading ? (
                      <div className="avatar-loader">Uploading...</div>
                    ) : (
                      <>
                        <img src={profileImage} alt="Avatar" className="profile-avatar-img" />
                        <div className="avatar-actions">
                          <div className="avatar-edit-icon" onClick={() => fileInputRef.current.click()} title="Change Photo">
                            <LuPencil size={14} color="white" />
                          </div>
                          {profileImage !== "icons/user.png" && !profileImage.includes("icons/user.png") && (
                            <div className="avatar-remove-icon" onClick={handleRemoveImage} title="Remove Photo">
                              <LuTrash2 size={14} color="white" />
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;

                      // Use S3 upload path "Users/"
                      const filename = `Users/${file.name}`;
                      setIsUploading(true);
                      setErrorMsg("");

                      try {
                        const token = localStorage.getItem("Authorization") || "";
                        const email = localStorage.getItem("userEmail") || "";

                        // 0. Delete old image if it exists
                        if (rawImageName && rawImageName !== "icons/user.png") {
                          try {
                            const deleteResponse = await fetch(`${config.API_BASE_URL}${config.DELETE_FILE_ENDPOINT}?filename=${encodeURIComponent(rawImageName)}`, {
                              method: 'DELETE',
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            if (!deleteResponse.ok) console.warn("Failed to delete old image");
                          } catch (delErr) {
                            console.error("Error deleting old profile image:", delErr);
                          }
                        }

                        // 1. Get Pre-signed URL
                        const presignedResponse = await fetch(
                          `${config.API_BASE_URL}${config.GENERATE_PRESIGNED_URL_ENDPOINT}?filename=${encodeURIComponent(filename)}`,
                          {
                            headers: { Authorization: `Bearer ${token}` }
                          }
                        );

                        if (!presignedResponse.ok) throw new Error("Failed to generate upload URL");
                        let uploadUrl = await presignedResponse.text();
                        if (uploadUrl.startsWith('"') && uploadUrl.endsWith('"')) uploadUrl = JSON.parse(uploadUrl);

                        // 2. Upload directly to S3
                        const s3Response = await fetch(uploadUrl, {
                          method: "PUT",
                          body: file,
                          headers: { "Content-Type": file.type }
                        });

                        if (!s3Response.ok) throw new Error("S3 Upload Failed");

                        // 3. Update Profile API using the new PUT API
                        const updateImageUrl = `${config.API_BASE_URL}${config.UPDATE_USER_IMAGE_ENDPOINT}?emailId=${email}&imageUrl=${filename}`;
                        
                        const updateResponse = await fetch(
                          updateImageUrl,
                          {
                            method: "PUT",
                            headers: {
                              Authorization: `Bearer ${token}`
                            }
                          }
                        );

                        if (!updateResponse.ok) throw new Error("Failed to update profile record");

                        setRawImageName(filename);
                        localStorage.setItem("rawProfileImage", filename);

                        // 4. Update UI Preview (State only, avoid localStorage for heavy base64)
                        const reader = new FileReader();
                        reader.onloadend = async () => {
                          const result = reader.result;
                          setProfileImage(result);
                          
                          // Instead of storing base64, fetch a fresh pre-signed URL for persistence
                          try {
                            const viewUrlResponse = await fetch(
                              `${config.API_BASE_URL}${config.VIEW_PRESIGNED_URL_ENDPOINT}?filename=${encodeURIComponent(filename)}`,
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            if (viewUrlResponse.ok) {
                              let viewUrl = await viewUrlResponse.text();
                              if (viewUrl.startsWith('"') && viewUrl.endsWith('"')) viewUrl = JSON.parse(viewUrl);
                              localStorage.setItem("profileImage", viewUrl);
                            }
                          } catch (cacheErr) {
                            console.error("Failed to cache pre-signed URL:", cacheErr);
                          }
                        };
                        reader.readAsDataURL(file);
                        setSuccessMsg("Profile picture updated successfully!");

                      } catch (err) {
                        console.error("Profile Image Upload Error:", err);
                        setErrorMsg(err.message || "Something went wrong during upload.");
                      } finally {
                        setIsUploading(false);
                      }
                    }}
                    style={{ display: "none" }}
                    accept="image/*"
                  />
                </div>

                <div className="profile-fields-grid">
                  <div className="profile-field">
                    <label>User Name</label>
                    <input type="text" value={userDetails.name} disabled />
                  </div>

                  <div className="profile-field">
                    <label>Phone Number</label>
                    <input type="text" value={`+91 ${userDetails.phone}`} disabled />
                  </div>

                  <div className="profile-field">
                    <label>Email ID</label>
                    <input type="text" value={userDetails.email} disabled />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab !== "Profile" && (
            <div className="tab-placeholder">
              <h3>{activeTab} Content</h3>
              <p>This section is under development.</p>
            </div>
          )}
        </div>
      </div>
      {successMsg && (
        <SuccessPopup message={successMsg} onClose={() => setSuccessMsg("")} />
      )}
      {errorMsg && (
        <ErrorPopup message={errorMsg} onClose={() => setErrorMsg("")} />
      )}
    </div>
  );
};

export default Profile;
